import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingCart } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";

const FloatingActions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { totalQuantity, flying, onFlyEnd, cartBump } = useCart();

  // Show ONLY for customer (role='user') authenticated users on shopping pages
  const hidden =
    !user ||
    user.role !== "user" ||
    ["/login", "/signup", "/driver-signup", "/mujahez-signup", "/forgot-password", "/cart", "/admin", "/sawakeen-jumlaty", "/driver", "/mujahez-jumlaty", "/my-orders"].some((p) =>
      location.pathname.startsWith(p)
    );

  if (hidden) return null;

  const cartTargetSelector = "#floating-cart-target";

  return (
    <>
      {/* Floating Cart (bottom-left in RTL = visually bottom-right) */}
      <motion.button
        id="floating-cart-target"
        data-testid="floating-cart"
        onClick={() => navigate("/cart")}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.92 }}
        key={`cart-${cartBump}`}
        className="fixed bottom-24 left-5 z-[90] w-14 h-14 bg-[#E85D45] text-white rounded-full shadow-2xl flex items-center justify-center hover:bg-[#d54d35] transition-colors group"
        style={{ boxShadow: "0 10px 30px -8px rgba(232,93,69,0.6)" }}
      >
        <motion.span
          animate={cartBump > 0 ? { scale: [1, 1.3, 1] } : {}}
          transition={{ duration: 0.4 }}
        >
          <ShoppingCart className="w-6 h-6" />
        </motion.span>
        {totalQuantity > 0 && (
          <motion.span
            key={totalQuantity}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            data-testid="floating-cart-count"
            className="absolute -top-1 -right-1 bg-white text-[#E85D45] text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center border-2 border-[#E85D45]"
          >
            {totalQuantity > 99 ? "99+" : totalQuantity}
          </motion.span>
        )}
      </motion.button>

      {/* Fly-to-cart animation */}
      <FlyingBubble flying={flying} onComplete={onFlyEnd} targetSelector={cartTargetSelector} />
    </>
  );
};

const FlyingBubble = ({ flying, onComplete, targetSelector }) => {
  if (!flying) return null;
  const targetEl = document.querySelector(targetSelector);
  const targetRect = targetEl?.getBoundingClientRect();
  const targetX = targetRect ? targetRect.left + targetRect.width / 2 : window.innerWidth - 40;
  const targetY = targetRect ? targetRect.top + targetRect.height / 2 : window.innerHeight - 40;

  const dx = targetX - flying.from.x;
  const dy = targetY - flying.from.y;

  return (
    <AnimatePresence>
      <motion.div
        key={flying.id}
        initial={{ x: flying.from.x - 24, y: flying.from.y - 24, scale: 1, opacity: 1 }}
        animate={{
          x: [flying.from.x - 24, flying.from.x - 24 + dx * 0.4, flying.from.x - 24 + dx],
          y: [flying.from.y - 24, flying.from.y - 24 + dy * 0.2 - 80, flying.from.y - 24 + dy],
          scale: [1, 1.1, 0.3],
          opacity: [1, 1, 0.2],
        }}
        transition={{ duration: 0.85, times: [0, 0.55, 1], ease: "easeIn" }}
        onAnimationComplete={onComplete}
        data-testid="flying-bubble"
        className="fixed top-0 left-0 z-[150] pointer-events-none w-12 h-12 rounded-full flex items-center justify-center font-bold text-white shadow-xl"
        style={{
          backgroundColor: flying.color,
          boxShadow: `0 0 20px ${flying.color}`,
        }}
      >
        <span className="text-lg drop-shadow">+</span>
      </motion.div>
    </AnimatePresence>
  );
};

export default FloatingActions;
